/**
 * Tab 04 · Insights — the material feed.
 *
 * The feed is built from content that already exists: 53 "aha" phrases (one per
 * pattern) and 40 research claims (five per direction, each with a named source).
 *
 * It is a daily drip, not a catalogue dump. A card becomes available one per day
 * counting from the account's start, so "updated daily" is literally true.
 * An older account simply has a longer feed. The unlock day gates and orders the
 * feed but is never printed — a date on a card only ages the material.
 *
 * Read cards dim but stay in place. Removing them turns the feed into a queue to
 * clear; dimming turns it into an archive you can walk back through.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { useT, useTt } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent, AppContent } from './content';
import { useProfile, Profile } from './profile';
import { useTasks, dayKey } from './tasks';
import { Icon } from './icons';
import { Appear, Body, Col, Fluid, H1, Meta, Muted, Panel, Rail, Row, useIsDesktop } from './primitives';
import { c, hair, hair2, f, scoreColor, DIR_ORDER, DESKTOP } from './tokens';
import { R } from './routes';

/** A thumbs-down hides that direction for two weeks. It ranks nothing. */
const MUTE_DAYS = 14;
const DAY = 86400000;

/** Days since epoch. Same value for every visit within a day — the shuffle seed. */
const dayIndex = () => Math.floor(Date.now() / DAY);

/**
 * Deterministic 0..1 from a card id and the day. Same order all day, a new one
 * tomorrow — no stored state, and nothing to keep in sync across devices.
 */
function dayOrder(id: string, seed: number): number {
    const M = 2147483647; // 2^31 - 1, prime
    let h = 1;

    for (let i = 0; i < id.length; i += 1) {
        h = (h * 48271 + id.charCodeAt(i)) % M;
    }
    // The day has to multiply, not add: adding it would move every card by the
    // same amount, which rotates the list instead of reshuffling it. Odd and
    // under 2^16 so h * m stays an exact integer in a double.
    const m = (seed % 30011) * 2 + 1;

    return ((h * m) % M) / M;
}

type Kind = 'aha' | 'research' | 'voice' | 'support';
/** One row of chips: read state and vote state share it — a card is in one bucket at a time. */
type StateFilter = 'all' | 'unread' | 'read' | 'up' | 'down';

/**
 * `accent` is the card's emphasis treatment, not a typeface — the feed is one
 * font. The kind is not labelled on the card: the direction tag carries the
 * header, and the emphasis already tells an aha from a research note.
 */
const KIND: Record<Kind, { accent: boolean }> = {
    research: { accent: false },
    aha: { accent: true },
    voice: { accent: false },
    support: { accent: true },
};

interface Card {
    id: string;
    kind: Kind;
    dirCode: string;
    title: string;
    lede?: string;
    body?: string[];
    take?: string;
    src?: string;
    patCode?: string;
    mine: boolean;
    /** Day this became available — derived, see the file header. */
    at: Date;
    /** Reading time as a `{key, n}` pair, resolved at render. */
    readMin: { key: string; n: number };
}

/**
 * Rough reading time from word count. Honest enough at this length. Returns a
 * translation key plus its count, since the caller renders in the active
 * language and this runs while the feed is being built.
 */
function readTime(parts: Array<string | undefined>): { key: string; n: number } {
    const words = parts.filter(Boolean).join(' ').split(/\s+/).length;

    if (words < 40) return { key: 'app.ins.read.sec', n: 30 };
    return { key: 'app.ins.read.min', n: Math.max(1, Math.round(words / 180)) };
}

function buildFeed(content: AppContent, profile: Profile, since: Date): Card[] {
    const minePat = new Set(profile.patterns.filter((p) => p.pct != null).map((p) => p.code));
    const measured = new Set(profile.measuredTracks);
    const draft: Array<Omit<Card, 'at' | 'readMin'>> = [];

    content.patterns.forEach((p) => {
        draft.push({
            id: `aha:${p.code}`,
            kind: 'aha',
            dirCode: p.dim,
            title: p.aha,
            lede: p.sub,
            body: p.mech,
            take: p.res[0],
            patCode: p.code,
            mine: minePat.has(p.code),
        });
    });

    content.directions.forEach((d) => {
        d.research.forEach(([claim, src], i) => {
            // The first sentence carries the finding; the rest is the detail.
            const cut = claim.indexOf('. ');
            const head = cut > 40 ? claim.slice(0, cut + 1) : claim;
            const tail = cut > 40 ? claim.slice(cut + 2) : '';

            draft.push({
                id: `res:${d.code}:${i}`,
                kind: 'research',
                dirCode: d.code,
                title: head,
                lede: tail,
                src,
                mine: measured.has(d.code),
            });
        });
    });

    // Personal material surfaces first, so the drip is about you before it is
    // about the model. This order fixes the unlock schedule below and never
    // changes — the daily reshuffle happens downstream, on display order only,
    // so nothing arrives early or disappears once it has landed.
    draft.sort((a, b) => Number(b.mine) - Number(a.mine));

    const today = Date.now();

    return draft
        .map((x, i) => ({
            ...x,
            at: new Date(since.getTime() + i * DAY),
            readMin: readTime([x.title, x.lede, ...(x.body || [])]),
        }))
        .filter((x) => x.at.getTime() <= today)
        .reverse();
}

const flatBtn: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
};

/** Always on the card, never behind a hover — the two buttons say enough alone. */
const Vote: React.FC<{ value?: 'up' | 'down'; onVote: (v: 'up' | 'down') => void }> = ({ value, onVote }) => {
    const t = useT();

    return (
        <Row style={{ gap: 8 }}>
            {(['up', 'down'] as const).map((v) => {
                const on = value === v;
                const col = v === 'up' ? '#4ade80' : '#f87171';

                return (
                    <button
                        key={v}
                        type="button"
                        aria-label={t(v === 'up' ? 'app.ins.vote.up' : 'app.ins.vote.down')}
                    onClick={() => onVote(v)}
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                        border: on ? `1px solid ${col}` : hair,
                        background: on ? `${col}18` : 'transparent',
                    }}
                >
                        <Icon k={v === 'up' ? 'thumbUp' : 'thumbDown'} size={14} color={on ? col : c.subtle} />
                    </button>
                );
            })}
        </Row>
    );
};

const FeedCard: React.FC<{
    card: Card;
    content: AppContent;
    profile: Profile;
    read: boolean;
    open: boolean;
    vote?: 'up' | 'down';
    onToggle: () => void;
    onRead: () => void;
    onVote: (v: 'up' | 'down') => void;
}> = ({ card, content, profile, read, open, vote, onToggle, onRead, onVote }) => {
    const { t, tn } = useTt();
    const k = KIND[card.kind];
    const d = content.dirBy[card.dirCode];
    const dirState = profile.directions.find((x) => x.code === card.dirCode);
    const dirHex = dirState?.deep ? scoreColor(dirState.score).hex : 'rgba(232,230,224,0.34)';
    const expandable = !!(card.body?.length || card.take);

    // One title style for every card. The kind is already legible from the tag
    // and the card's own emphasis — it does not need a second typeface.
    const titleStyle: React.CSSProperties = {
        fontFamily: f.serif,
        fontWeight: 300,
        fontSize: k.accent ? 18.5 : 17,
        lineHeight: 1.32,
        letterSpacing: '-0.02em',
        color: c.textPri,
        marginTop: 13,
    };

    return (
        <div
            style={{
                borderRadius: 17,
                padding: k.accent ? '19px 18px' : '15px 16px',
                border: k.accent ? '1px solid rgba(232,230,224,0.12)' : hair,
                background: k.accent ? `linear-gradient(180deg, rgba(232,230,224,0.035), ${c.card} 70%)` : c.card,
                // Read stays visible, just quieter — the feed is an archive.
                opacity: read && !open ? 0.55 : 1,
                transition: 'opacity 250ms',
                minWidth: 0,
                breakInside: 'avoid',
                marginBottom: 11,
            }}
        >
            <Row style={{ gap: 10 }}>
                <Link to={R.dir(card.dirCode)} style={{ textDecoration: 'none' }}>
                    <Row style={{ gap: 7 }}>
                        <Icon k={card.dirCode} size={12} color={dirHex} />
                        <Meta style={{ fontSize: 10 }}>{d?.disp}</Meta>
                    </Row>
                </Link>
                <Meta style={{ fontSize: 10, color: c.subtle, marginLeft: 'auto' }}>
                    {tn(card.readMin.key, card.readMin.n)}
                </Meta>
            </Row>

            <div style={titleStyle}>{card.title}</div>

            {card.patCode ? (
                <Meta style={{ fontSize: 10, color: c.subtle, display: 'block', marginTop: 12 }}>
                    {t('app.pats.one').toUpperCase()} · {content.patBy[card.patCode]?.name}
                </Meta>
            ) : (
                card.lede && <Muted style={{ fontSize: 13, marginTop: 8 }}>{card.lede}</Muted>
            )}

            {expandable && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateRows: open ? '1fr' : '0fr',
                        transition: 'grid-template-rows 420ms cubic-bezier(.4,0,.2,1)',
                    }}
                >
                    <div style={{ overflow: 'hidden' }}>
                        <Col style={{ marginTop: 13 }} gap={11}>
                            {(card.body || []).map((para) => (
                                <Body key={para.slice(0, 24)} style={{ fontSize: 13.5 }}>
                                    {para}
                                </Body>
                            ))}
                            {card.take && (
                                <div
                                    style={{
                                        marginTop: 2,
                                        paddingLeft: 13,
                                        borderLeft: '2px solid rgba(232,230,224,0.2)',
                                    }}
                                >
                                    <Meta style={{ fontSize: 9, letterSpacing: '0.14em', color: c.subtle }}>
                                        {t('app.pats.takeaway')}
                                    </Meta>
                                    <div
                                        style={{
                                            fontFamily: f.serif,
                                            fontWeight: 300,
                                            fontSize: 15.5,
                                            lineHeight: 1.4,
                                            color: c.textPri,
                                            marginTop: 7,
                                        }}
                                    >
                                        {card.take}
                                    </div>
                                </div>
                            )}
                        </Col>
                    </div>
                </div>
            )}

            <Row style={{ gap: 8, marginTop: 14, paddingTop: 12, borderTop: hair2, flexWrap: 'wrap' }}>
                {card.src && (
                    <Meta
                        style={{
                            fontSize: 10,
                            color: c.subtle,
                            letterSpacing: '0.04em',
                            textTransform: 'none',
                            minWidth: 0,
                            flex: '1 1 60px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {card.src}
                    </Meta>
                )}
                <Row style={{ gap: 10, marginLeft: 'auto', flex: '0 0 auto' }}>
                    <Vote value={vote} onVote={onVote} />
                    {/* Read is its own toggle, so short cards without a body can be marked too. */}
                    <button type="button" aria-pressed={read} onClick={onRead} style={flatBtn}>
                        <Icon k="check" size={12} color={read ? '#4ade80' : c.subtle} />
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em', color: read ? c.text : c.subtle }}>
                            {t('app.ins.read')}
                        </Meta>
                    </button>
                    {expandable && (
                        <button type="button" onClick={onToggle} style={flatBtn}>
                            <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em', color: c.subtle }}>
                                {t(open ? 'app.ins.less' : 'app.ins.more')}
                            </Meta>
                            <Icon
                                k="down"
                                size={12}
                                color={c.subtle}
                                style={{
                                    transform: open ? 'rotate(180deg)' : undefined,
                                    transition: 'transform 300ms',
                                }}
                            />
                        </button>
                    )}
                </Row>
            </Row>
        </div>
    );
};

const FilterRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Row style={{ gap: 12, alignItems: 'flex-start' }}>
        <Meta
            style={{
                fontSize: 9,
                letterSpacing: '0.16em',
                color: c.subtle,
                width: 58,
                flex: '0 0 auto',
                paddingTop: 7,
            }}
        >
            {label}
        </Meta>
        <Row style={{ gap: 5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>{children}</Row>
    </Row>
);

const PickChip: React.FC<{
    on: boolean;
    dim?: boolean;
    icon?: string;
    iconColor?: string;
    label: string;
    n?: number;
    onClick: () => void;
}> = ({ on, dim, icon, iconColor, label, n, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
    >
        <Row
            style={{
                gap: 5,
                padding: '6px 11px',
                borderRadius: 99,
                background: on ? c.textPri : c.card,
                border: on ? `1px solid ${c.textPri}` : hair,
                opacity: dim ? 0.4 : 1,
            }}
        >
            {icon && <Icon k={icon} size={11} color={on ? '#0a0a0a' : iconColor || 'rgba(232,230,224,0.4)'} />}
            <Meta style={{ fontSize: 9.5, letterSpacing: '0.1em', color: on ? '#0a0a0a' : c.muted }}>{label}</Meta>
            {n != null && (
                <span style={{ fontFamily: f.mono, fontSize: 9, color: on ? 'rgba(10,10,10,0.55)' : c.subtle }}>
                    {n}
                </span>
            )}
        </Row>
    </button>
);

const InsightsPage: React.FC = () => {
    const { t, tn } = useTt();
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const { list: tasks } = useTasks();
    const desktop = useIsDesktop(DESKTOP);

    const [stateFilter, setStateFilter] = React.useState<StateFilter>('all');
    const [dirFilter, setDirFilter] = React.useState('all');
    const [openId, setOpenId] = React.useState<string | null>(null);
    const [heroClosed, setHeroClosed] = React.useState(false);

    // Read state and votes live in `user_insights` — one row per touched card,
    // holding the CURRENT state (see migrations/2026-08-05_user_insights.sql).
    // No localStorage: a second copy per device is how the counts diverged.
    const [ins, setIns] = React.useState<
        Record<string, { action: 'up' | 'down' | 'none'; actedAt: number; read: boolean }>
    >({});

    React.useEffect(() => {
        if (!user?.id) return undefined;
        let live = true;

        supabase
            .from('user_insights')
            .select('insight_id, action, acted_at, read_at')
            .then(({ data, error }) => {
                if (!live || error || !data) return;
                const next: typeof ins = {};

                data.forEach((r: any) => {
                    next[r.insight_id] = {
                        action: r.action,
                        actedAt: r.acted_at ? new Date(r.acted_at).getTime() : 0,
                        read: !!r.read_at,
                    };
                });
                setIns(next);
            });
        return () => {
            live = false;
        };
    }, [user?.id]);

    // Every write sends the WHOLE row: a partial upsert would reset the other
    // half (toggling read must not clear a vote), and the client already holds
    // the full state it is writing.
    const mutate = React.useCallback(
        (id: string, fn: (cur: { action: 'up' | 'down' | 'none'; actedAt: number; read: boolean }) => {
            action: 'up' | 'down' | 'none';
            actedAt: number;
            read: boolean;
        }) => {
            setIns((prev) => {
                const cur = prev[id] || { action: 'none' as const, actedAt: 0, read: false };
                const next = fn(cur);

                if (next === cur) return prev;
                if (user?.id) {
                    supabase
                        .from('user_insights')
                        .upsert(
                            {
                                user_id: user.id,
                                insight_id: id,
                                action: next.action,
                                acted_at: new Date(next.actedAt || Date.now()).toISOString(),
                                read_at: next.read ? new Date().toISOString() : null,
                            },
                            { onConflict: 'user_id,insight_id' },
                        )
                        .then(() => undefined);
                }
                return { ...prev, [id]: next };
            });
        },
        [user?.id],
    );

    const markRead = React.useCallback(
        (id: string) => mutate(id, (cur) => (cur.read ? cur : { ...cur, read: true })),
        [mutate],
    );

    const toggleRead = React.useCallback((id: string) => mutate(id, (cur) => ({ ...cur, read: !cur.read })), [mutate]);

    // A vote of either kind marks the card read — you can't judge it unread.
    // Un-voting leaves it read.
    const castVote = React.useCallback(
        (id: string, v: 'up' | 'down') =>
            mutate(id, (cur) => ({ action: cur.action === v ? 'none' : v, actedAt: Date.now(), read: true })),
        [mutate],
    );

    const votes = React.useMemo(() => {
        const out: Record<string, { v: 'up' | 'down'; at: number }> = {};

        Object.entries(ins).forEach(([id, s]) => {
            if (s.action !== 'none') out[id] = { v: s.action, at: s.actedAt };
        });
        return out;
    }, [ins]);

    const read = React.useMemo(
        () =>
            new Set(
                Object.entries(ins)
                    .filter(([, s]) => s.read)
                    .map(([id]) => id),
            ),
        [ins],
    );

    // Drip start: the account's own start date, so feed length is the user's
    // history rather than a global clock.
    const since = React.useMemo(() => {
        const t = userProfile?.created_at ? new Date(userProfile.created_at) : null;

        return t && !Number.isNaN(t.getTime()) ? t : new Date(Date.now() - 6 * DAY);
    }, [userProfile?.created_at]);

    const feed = React.useMemo(() => (content ? buildFeed(content, profile, since) : []), [content, profile, since]);

    // A thumbs-down hides the whole direction for two weeks. That is the only
    // thing a vote does — there is no ranking behind it, and the explainer in
    // the rail says so.
    const muted = React.useMemo(() => {
        const out = new Set<string>();

        feed.forEach((card) => {
            const v = votes[card.id];

            if (v?.v === 'down' && Date.now() - v.at < MUTE_DAYS * DAY) out.add(card.dirCode);
        });
        return out;
    }, [feed, votes]);

    const visible = React.useMemo(() => feed.filter((x) => !muted.has(x.dirCode) || votes[x.id]), [feed, muted, votes]);

    // The set is fixed by the drip; only the order moves, once a day. Yours stay
    // ahead of the rest, so the hero rotates among your own patterns.
    const day = dayIndex();
    const ordered = React.useMemo(
        () => [...visible].sort((a, b) => Number(b.mine) - Number(a.mine) || dayOrder(a.id, day) - dayOrder(b.id, day)),
        [visible, day],
    );

    const hero = heroClosed ? null : ordered.find((x) => x.mine) || ordered[0];
    const rest = React.useMemo(
        () =>
            ordered
                .filter((x) => x.id !== hero?.id)
                .filter((x) => (dirFilter === 'all' ? true : x.dirCode === dirFilter))
                .filter((x) => {
                    if (stateFilter === 'unread') return !read.has(x.id);
                    if (stateFilter === 'read') return read.has(x.id);
                    if (stateFilter === 'up' || stateFilter === 'down') return votes[x.id]?.v === stateFilter;
                    return true;
                }),
        [ordered, hero, dirFilter, stateFilter, read, votes],
    );

    const readCount = visible.filter((x) => read.has(x.id)).length;
    const likedCount = visible.filter((x) => votes[x.id]?.v === 'up').length;
    const dislikedCount = visible.filter((x) => votes[x.id]?.v === 'down').length;
    const perDir = React.useMemo(() => {
        const m: Record<string, number> = {};

        visible.forEach((x) => {
            m[x.dirCode] = (m[x.dirCode] || 0) + 1;
        });
        return m;
    }, [visible]);

    /** Sources grouped by direction — the honest version of "where this comes from". */
    const sources = React.useMemo(() => {
        if (!content) return [];
        const out: Array<{ src: string; dir: string }> = [];
        const seen = new Set<string>();

        content.directions.forEach((d) => {
            d.research.forEach(([, src]) => {
                if (seen.has(src)) return;
                seen.add(src);
                out.push({ src, dir: d.disp });
            });
        });
        return out.slice(0, 8);
    }, [content]);

    const weekDone = React.useMemo(() => {
        // Day keys are written in local time; `new Date(key)` reads them as UTC
        // midnight, which shifts the window by a day west of Greenwich. ISO days
        // sort lexicographically, so compare the strings.
        const from = dayKey(new Date(Date.now() - 6 * DAY));

        return tasks.reduce(
            (n, t) => n + Object.entries(t.log || {}).filter(([day, e]) => e.done && day >= from).length,
            0,
        );
    }, [tasks]);

    const shellProps = {
        active: 'ins' as const,
        title: t('app.tab.ins'),
        subtitle: t('app.ins.subtitle'),
        index: profile.index,
        email: userProfile?.email || user?.email,
        chips: (
            <Row style={{ gap: 8, padding: '7px 13px', borderRadius: 99, border: hair, background: c.card }}>
                <Icon k="tIns" size={12} color={c.muted} />
                <Meta style={{ fontSize: 10 }}>
                    {t('app.ins.counts', { total: visible.length, read: readCount })}
                </Meta>
            </Row>
        ),
    };

    if (!content) {
        return (
            <AppShell {...shellProps}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }}>
                    <Meta style={{ color: c.subtle }}>{t('app.state.loading')}</Meta>
                </Col>
            </AppShell>
        );
    }

    const filters = (
        <Col gap={9}>
            <FilterRow label={t('app.ins.filter.state')}>
                <PickChip
                    on={stateFilter === 'all'}
                    label={t('app.ins.filter.all')}
                    n={visible.length}
                    onClick={() => setStateFilter('all')}
                />
                <PickChip
                    on={stateFilter === 'unread'}
                    label={t('app.ins.filter.unread')}
                    n={visible.length - readCount}
                    onClick={() => setStateFilter('unread')}
                />
                <PickChip
                    on={stateFilter === 'read'}
                    label={t('app.ins.filter.read')}
                    n={readCount}
                    onClick={() => setStateFilter('read')}
                />
                <PickChip
                    on={stateFilter === 'up'}
                    dim={!likedCount}
                    icon="thumbUp"
                    iconColor="#4ade80"
                    label={t('app.ins.filter.liked')}
                    n={likedCount || undefined}
                    onClick={() => setStateFilter('up')}
                />
                <PickChip
                    on={stateFilter === 'down'}
                    dim={!dislikedCount}
                    icon="thumbDown"
                    iconColor="#f87171"
                    label={t('app.ins.filter.disliked')}
                    n={dislikedCount || undefined}
                    onClick={() => setStateFilter('down')}
                />
            </FilterRow>
            <FilterRow label={t('app.ins.filter.direction')}>
                <PickChip on={dirFilter === 'all'} label={t('app.ins.filter.all8')} onClick={() => setDirFilter('all')} />
                {DIR_ORDER.map((code) => {
                    const st = profile.directions.find((x) => x.code === code);
                    const n = perDir[code] || 0;

                    return (
                        <PickChip
                            key={code}
                            on={dirFilter === code}
                            dim={n === 0}
                            icon={code}
                            iconColor={st?.deep ? scoreColor(st.score).hex : 'rgba(232,230,224,0.34)'}
                            label={content.dirBy[code]?.disp || code}
                            n={n || undefined}
                            onClick={() => setDirFilter(dirFilter === code ? 'all' : code)}
                        />
                    );
                })}
            </FilterRow>
        </Col>
    );

    const heroDirState = hero ? profile.directions.find((x) => x.code === hero.dirCode) : undefined;

    const heroBlock = hero && (
        <div
            style={{
                border: '1px solid #f59e0b33',
                background: `linear-gradient(140deg, #f59e0b12, ${c.card} 55%)`,
                borderRadius: 18,
                padding: desktop ? '22px 24px' : '18px 18px',
                minWidth: 0,
                marginBottom: 11,
            }}
        >
            <Row style={{ gap: 9 }}>
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: '#f59e0b',
                        boxShadow: '0 0 8px #f59e0b',
                    }}
                />
                <Meta style={{ color: '#f59e0b', fontSize: 10, letterSpacing: '0.16em' }}>
                    {t('app.state.insight')}
                </Meta>
                <Meta style={{ fontSize: 10, color: c.subtle, marginLeft: 'auto' }}>
                    {tn(hero.readMin.key, hero.readMin.n)}
                </Meta>
                <button
                    type="button"
                    aria-label={t('app.action.hide')}
                    onClick={() => setHeroClosed(true)}
                    style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        border: hair,
                        background: 'transparent',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <Icon k="close" size={10} color={c.subtle} />
                </button>
            </Row>

            <div
                style={{
                    fontFamily: f.serif,
                    fontWeight: 300,
                    fontSize: desktop ? 30 : 26,
                    lineHeight: 1.18,
                    letterSpacing: '-0.02em',
                    color: c.textPri,
                    marginTop: 14,
                }}
            >
                {hero.title}
            </div>
            {hero.lede && <Body style={{ fontSize: 14.5, marginTop: 13 }}>{hero.lede}</Body>}

            {!!hero.body?.length && (
                <div style={{ marginTop: 16, columnCount: desktop ? 2 : 1, columnGap: 30 }}>
                    {hero.body.slice(0, 2).map((para) => (
                        <Muted key={para.slice(0, 24)} style={{ fontSize: 13, marginBottom: 10, breakInside: 'avoid' }}>
                            {para}
                        </Muted>
                    ))}
                </div>
            )}

            <Row style={{ gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                <Link
                    to={hero.patCode ? R.pattern(hero.patCode) : R.dir(hero.dirCode)}
                    style={{ textDecoration: 'none' }}
                >
                    <Row style={{ gap: 8 }}>
                        <Meta style={{ color: '#f59e0b', fontSize: 10.5, letterSpacing: '0.14em' }}>
                            {t('app.ins.readFull')}
                        </Meta>
                        <Icon k="arrow" size={14} color="#f59e0b" />
                    </Row>
                </Link>
                <Row style={{ gap: 12, marginLeft: 'auto', flex: '0 0 auto' }}>
                    <Link to={R.dir(hero.dirCode)} style={{ textDecoration: 'none' }}>
                        <Row style={{ gap: 7 }}>
                            <Icon
                                k={hero.dirCode}
                                size={12}
                                color={
                                    heroDirState?.deep ? scoreColor(heroDirState.score).hex : 'rgba(232,230,224,0.34)'
                                }
                            />
                            <Meta style={{ fontSize: 10 }}>{content.dirBy[hero.dirCode]?.disp}</Meta>
                        </Row>
                    </Link>
                    <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(232,230,224,0.12)' }} />
                    <Vote value={votes[hero.id]?.v} onVote={(v) => castVote(hero.id, v)} />
                </Row>
            </Row>
        </div>
    );

    const list = rest.length ? (
        <div style={desktop ? { columnCount: 2, columnGap: 11 } : undefined}>
            {rest.map((card) => (
                <FeedCard
                    key={card.id}
                    card={card}
                    content={content}
                    profile={profile}
                    read={read.has(card.id)}
                    open={openId === card.id}
                    vote={votes[card.id]?.v}
                    onToggle={() => {
                        const next = openId === card.id ? null : card.id;

                        setOpenId(next);
                        if (next) markRead(card.id);
                    }}
                    onRead={() => toggleRead(card.id)}
                    onVote={(v) => castVote(card.id, v)}
                />
            ))}
        </div>
    ) : (
        <Muted style={{ fontSize: 13 }}>{t('app.ins.filter.empty')}</Muted>
    );

    const railHow = (
        <Panel title={t('app.state.how')} pad="16px 18px 18px">
            <Muted style={{ fontSize: 13 }}>{t('app.ins.how')}</Muted>
        </Panel>
    );

    const railWeek = (
        <Panel title={t('app.ins.week')} pad="6px 18px 14px">
            {(
                [
                    [
                        t('app.ins.week.tests'),
                        String(profile.measuredTracks.length),
                        profile.measuredTracks.length
                            ? t('app.ins.week.testsHint', { n: profile.measuredTracks.length })
                            : null,
                    ],
                    [t('app.ins.week.tasks'), String(tasks.length), null],
                    [t('app.ins.week.steps'), String(weekDone), null],
                    [t('app.ins.week.insights'), String(readCount), null],
                ] as Array<[string, string, string | null]>
            ).map(([label, v, hint], i) => (
                <Row key={label} style={{ gap: 12, padding: '13px 0', borderTop: i ? hair2 : 'none' }}>
                    <Col style={{ flex: 1, minWidth: 0 }} gap={3}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em' }}>{label}</Meta>
                        {hint && <Muted style={{ fontSize: 11 }}>{hint}</Muted>}
                    </Col>
                    <span style={{ fontFamily: f.mono, fontSize: 18, fontWeight: 300, color: c.textPri }}>{v}</span>
                </Row>
            ))}
        </Panel>
    );

    const railSources = (
        <Panel title={t('app.ins.sources')} pad="16px 18px 14px">
            <Muted style={{ fontSize: 12.5 }}>{t('app.ins.sources.note')}</Muted>
            <Col style={{ marginTop: 14 }} gap={0}>
                {sources.map((s, i) => (
                    <Row key={s.src} style={{ gap: 12, padding: '10px 0', borderTop: i ? hair2 : 'none' }}>
                        <span
                            style={{
                                fontSize: 12.5,
                                color: c.text,
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {s.src}
                        </span>
                        <Meta style={{ fontSize: 9, color: c.subtle, flex: '0 0 auto' }}>{s.dir}</Meta>
                    </Row>
                ))}
            </Col>
        </Panel>
    );

    const footNote = (
        <Muted style={{ fontSize: 12 }}>{t('app.ins.footnote')}</Muted>
    );

    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <Appear>
                    <div style={{ padding: '18px 34px 0' }}>{filters}</div>
                    <DeskRow>
                        <Fluid>
                            {heroBlock}
                            {list}
                            {footNote}
                        </Fluid>
                        <Rail width={340}>
                            {railHow}
                            {railWeek}
                            {railSources}
                        </Rail>
                    </DeskRow>
                </Appear>
            </AppShell>
        );
    }

    return (
        <AppShell {...shellProps}>
            <Appear>
                <div style={{ padding: '10px 20px 0' }}>
                    <H1>{t('app.tab.ins')}</H1>
                </div>
                <div style={{ padding: '18px 20px 0' }}>{filters}</div>
                <Col style={{ padding: '18px 20px 26px' }} gap={11}>
                    {heroBlock}
                    {list}
                    {railHow}
                    {railWeek}
                    {footNote}
                </Col>
            </Appear>
        </AppShell>
    );
};

export default InsightsPage;
